/**
 * Página `/finanzas-aseador` — vista del aseador sobre su propio sueldo.
 *
 * Lo que ve: por mes (últimos 6 por default; admite 3/6/12), el desglose
 * de cada aseo con sueldo + bono - descuento = neto, y los totales del mes
 * separando "ya pagado" vs "pendiente". No ve precios cobrados al cliente,
 * ni finanzas de la empresa, ni datos de huéspedes — sólo lo que afecta su
 * propio bolsillo.
 *
 * Backend: `GET /api/finanzas/aseador/me?months=N` (controller
 * `misFinanzasAseador`) — toma el id del JWT, sin path param para evitar
 * IDOR. Ver `docs/rol-aseador.md` §10 "Finanzas".
 *
 * UI compartida con la vista admin (`/finanzas-aseador-admin`) vía el
 * componente `<FinanzasAseadorView />`. Esta página solo se ocupa del
 * fetch (con id del JWT) y del header. Ver `docs/modulo-finanzas-aseador.md`.
 */

import { useCallback, useEffect, useState } from "react";
import type { Dayjs } from "dayjs";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { finanzasApi } from "../../services/api";
import type { MesFinanzaAseador } from "../../services/finanzas.api";
import FinanzasAseadorView from "./FinanzasAseadorView";
import "./finanzasAseador.css";

export default function FinanzasAseador() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const { socket } = useWebSocket();

  // Modo SEMANAS (round 2026-07-06): los pagos al aseador son semanales (L-D,
  // los lunes), así que "Mis ingresos" muestra buckets semanales. `weeks` = N
  // semanas hacia atrás; la vista las agrupa por mes (`groupByMonth`).
  const [weeks, setWeeks] = useState<number>(12);
  // Modo MES PUNTUAL (round 2026-07-09): si el aseador elige un mes/año, se
  // pide ese mes desplegado en sus semanas L-D (backend `?month=YYYY-MM`).
  // `null` = modo "últimas N semanas". Tiene precedencia sobre `weeks`.
  const [specificMonth, setSpecificMonth] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meses, setMeses] = useState<MesFinanzaAseador[]>([]);

  const load = useCallback(async (w: number, month: Dayjs | null) => {
    setLoading(true);
    setError(null);
    const resp = month
      ? await finanzasApi.getMisFinanzasAseador({ month: month.format("YYYY-MM") })
      : await finanzasApi.getMisFinanzasAseador({ weeks: w });
    if (!resp) {
      setError("No pude cargar tus finanzas. Intenta de nuevo.");
      setMeses([]);
    } else {
      // Mostrar más reciente arriba.
      setMeses([...resp.data.meses].reverse());
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load(weeks, specificMonth);
  }, [load, weeks, specificMonth]);

  // Suscripción WS: cuando admin aprueba/crea/edita un movimiento que toca
  // a este aseador, refrescamos. El broadcast filtra por `aseadorId` en
  // backend, pero defendemos también acá.
  useEffect(() => {
    if (!socket || !user?.id) return;
    const handler = (ev: MessageEvent) => {
      try {
        const msg = JSON.parse(ev.data);
        if (
          msg?.type === "ASEADOR_LEDGER_CHANGED" &&
          Number(msg.aseadorId) === Number(user.id)
        ) {
          void load(weeks, specificMonth);
        }
      } catch {
        // Ignorar mensajes mal formados.
      }
    };
    socket.addEventListener("message", handler);
    return () => socket.removeEventListener("message", handler);
  }, [socket, user?.id, load, weeks, specificMonth]);

  return (
    <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
      <div className="fa-page">
        <PageHeader
          title="Mis ingresos"
          helpText={
            <>
              <div>
                Tus ingresos <b>por semana</b> (lunes a domingo), agrupados por
                mes. El neto de cada aseo es tu <code>sueldo del aseo</code>.
              </div>
              <div style={{ marginTop: 6 }}>
                Pago canónico: <b>los lunes</b> se paga lo trabajado entre el
                lunes y domingo de la semana anterior. Por eso la vista es
                semanal (L-D). El rango <b>8 / 12 / 24 semanas</b> va desde la{" "}
                <b>próxima semana</b> hacia atrás; para revisar aseos de meses
                más lejanos usa <b>“Mes específico”</b> (permite meses futuros).
              </div>
              <div style={{ marginTop: 8 }}>
                <b>Qué significa cada etiqueta de un aseo:</b>
                <ul style={{ margin: "4px 0 0", paddingInlineStart: 18 }}>
                  <li>
                    <b>Programado</b>: asignado con fecha <b>futura</b>, aún no
                    ocurre. Es un <b>estimado</b> — no es un pago confirmado
                    hasta que se realice (y si te lo desasignan o cambian,
                    desaparece o se actualiza solo).
                  </li>
                  <li>
                    <b>Agendado</b>: asignado, aún no iniciado.
                  </li>
                  <li>
                    <b>En proceso</b>: lo estás realizando.
                  </li>
                  <li>
                    <b>Completado (por pagar)</b>: terminado; entra al próximo
                    pago del lunes.
                  </li>
                  <li>
                    <b>Pagado</b>: ya se te liquidó.
                  </li>
                </ul>
              </div>
              <div style={{ marginTop: 6 }}>
                <b>Movimientos extra</b> (bonos, multas, adelantos, propinas,
                reembolsos que registra admin): <b>Pendiente</b> = por aprobar,{" "}
                <b>Aprobado</b> = aprobado/pagado, <b>Denegado</b> = rechazado
                (no suma).
              </div>
            </>
          }
        />

        <FinanzasAseadorView
          meses={meses}
          months={0}
          weeks={weeks}
          onChangeWeeks={(w) => {
            // Elegir un rango de semanas sale del modo "mes puntual".
            setSpecificMonth(null);
            setWeeks(w);
          }}
          groupByMonth
          selectedMonth={specificMonth}
          onSelectMonth={setSpecificMonth}
          loading={loading}
          error={error}
          onChangeMonths={() => {}}
          onReload={() => void load(weeks, specificMonth)}
        />
      </div>
    </Layout>
  );
}
